/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, NavLink } from 'react-router-dom'
import styled from 'styled-components'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import Spinner from 'components/Spinner'
import ArrowIcon from '@mui/icons-material/TrendingFlat'

import { PageTitle, screenSize } from 'styles/shared'
import { color } from 'styles/colors'
import Brand from 'helpers/brand'

import GA4 from 'helpers/ga4'
import usePaywallPlans from 'hooks/usePaywallPlans'
import usePaywallActiveAcct from 'hooks/usePaywallActiveAcct'
import FeaturesBox from 'pages/paywall/FeaturesBox'
import PlanSelection from 'pages/paywall/PlanSelection'

const UNLOCK_TITLE = 'Unlock Next-Level Diagnostics with Premium Access'
const PREMIUM_EXPLANATION = 'Upgrade to Diagnostics Premium and harness cutting-edge features designed to give you more diagnostics data, improve technician service efficiency, and enhance homeowner communication – all-in-one seamless platform.'
const WHY_TITLE = 'Why upgrade?'
const WHY_UPGRADE = [
  'Extended Runtime History - go beyond 30 days, to track performance month-over-month and year-over-year for deeper insights and trend analysis.',
  'Trane Tech Assistant - get AI-powered guided service assistance in the field on Technician App, helping you troubleshoot faster and smarter.',
  'Homeowner Reports - generate co-branded, ready-to-share reports that build trust and transparency with homeowners.',
  'Maintenance Trends - analyze system behavior across indoor, outdoor and operational metrics for more accurate diagnostics and proactive servicing.'
]
const COMING_SOON_TITLE = 'More features coming soon!'
const COMING_SOON = 'We’re continuously adding new features to expand your diagnostics capabilities. Future updates will be included at no extra costs –– ensuring immediate access to the latest advancements Trane has to offer.'
const PRICING_TITLE = 'Exclusive pricing'
const PRICING = [
  'USD $250/month – flexible, cancel anytime',
  'USD $2,400/ year – save USD $600 with an annual subscription'
]
const PaywallOptions = () => {
  const { dealerId } = useParams()
  const navigate = useNavigate()

  const { plans, yearlyPlanNo, monthlyPlanNo, arePaywallPlansLoaded } = usePaywallPlans(dealerId)
  const { account, isPaywallAcctLoaded } = usePaywallActiveAcct(dealerId)

  const [selectedInput, setSelectedInput] = useState(PlanSelection.YEARLY)
  const [currentPlan, setCurrentPlan] = useState(yearlyPlanNo)
  const [planType, setPlanType] = useState<string>('diag_yearly')

  useEffect(() => {
    if (arePaywallPlansLoaded && isPaywallAcctLoaded) {
      if (account) {
        for (let i = 0; i < plans.length; i++) {
          if (plans[i].no.toString() === account.planNo) {
            setPlanType(plans[i].planId)
            break
          }
        }
      }

      if (planType === 'diag_yearly') {
        setSelectedInput(PlanSelection.YEARLY)
        setCurrentPlan(yearlyPlanNo)
      } else {
        setSelectedInput(PlanSelection.MONTHLY)
        setCurrentPlan(monthlyPlanNo)
      }
    }
  }, [account, arePaywallPlansLoaded, plans, isPaywallAcctLoaded, monthlyPlanNo, yearlyPlanNo]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!arePaywallPlansLoaded || !isPaywallAcctLoaded) {
    return <Spinner variant="page" />
  }

  const onChangeSelectedInput = (value: number) => {
    setSelectedInput(Number(value))

    if (Number(value) === PlanSelection.YEARLY) {
      setCurrentPlan(yearlyPlanNo)
      setPlanType('diag_yearly')
    } else {
      setCurrentPlan(monthlyPlanNo)
      setPlanType('diag_monthly')
    }
  }

  const onClickUpgrade = () => {
    GA4.sendEvent({ category: 'Dealer Paywall', label: 'Dealer Paywall Updated', action: 'Click Upgrade' })

    navigate(`/paywall/account/${dealerId}/${currentPlan}/${planType}`)
  }

  return (
    <>
      <S.Top>
        <NavLink to={'/dashboard/'} >
          <S.ArrowIcon fontSize="large" />
        </NavLink>
        <S.ProductLogo src={Brand.premiumProductLogoPath()} alt={`${Brand.brandName()} logo`} />
        <div />
      </S.Top>
      <S.Content>
        <S.Form>
          <S.LeftSide>
            <S.FormSection>
              <S.SectionHeader>{UNLOCK_TITLE}</S.SectionHeader>
              <S.Explanation>{PREMIUM_EXPLANATION}</S.Explanation>
            </S.FormSection>
            <S.FormSection>
              <S.SectionHeader>{WHY_TITLE}</S.SectionHeader>
              {WHY_UPGRADE.map((reason) => (
                <S.Explanation key={reason}>
                  <S.Image src={'/img/checkmark.png'} width={40} />
                  <span>
                    <b>
                      {reason.split(' -')[0]}
                    </b>
                    <span> -</span>
                    {reason.split(' -')[1]}
                  </span>
                </S.Explanation>
              ))}
            </S.FormSection>
            <S.FormSection>
              <S.SectionHeader>{COMING_SOON_TITLE}</S.SectionHeader>
              <S.Explanation>{COMING_SOON}</S.Explanation>
            </S.FormSection>
            <S.FormSection>
              <S.SectionHeader>{PRICING_TITLE}</S.SectionHeader>
              {PRICING.map((price) => (
                <S.Explanation key={price}>
                  <S.PriceArrowIcon fontSize="large" />
                  {price}
                </S.Explanation>
              ))}
            </S.FormSection>
          </S.LeftSide>
          <S.RightSide>
            <S.FormSection>
              <S.RightSideHeader>Experience the difference</S.RightSideHeader>
              <S.Features>
                <FeaturesBox isBasicList onChangeSelection={onChangeSelectedInput} selectedInput={selectedInput} />
                <FeaturesBox isBasicList={false} onChangeSelection={onChangeSelectedInput} selectedInput={selectedInput} />
              </S.Features>
            </S.FormSection>
          </S.RightSide>
        </S.Form>
        <S.Bottom>
          <Button decoration="positive" text="Upgrade" onPress={onClickUpgrade}
            styles={{ button: { height: 46, borderRadius: 10, width: 200 } }}
          />
        </S.Bottom>
      </S.Content>
    </>
  )
}

export default PaywallOptions

const S = {
  Content: styled.div`
    padding: 50px;
    background-image: url(${Brand.premiumBackgroundPath()});
    background-size: 100% 100%;
    background-repeat: no-repeat;
  `,
  ArrowIcon: styled(ArrowIcon)`
    transform: rotate(180deg);
    color: ${color('breadCrumbsTextHover')};
  `,
  Bottom: styled.div`
    display: flex;
    flex-direction: column;
    padding: 10px;
  `,
  PriceArrowIcon: styled(ArrowIcon)`
    color: #2C7AC2;
    margin: -5px 8px;
  `,
  Container: styled.div`
    padding: 20px;
  `,
  Features: styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 10px;
    @media ${screenSize.small} {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 1145px;
    }
  `,
  Form: styled.form`
    display: flex;
    grid-column-gap: 50px;
    flex-wrap: wrap;
    justify-content: space-between;
  `,
  FormSection: styled.div`
    max-width: 720px;
    & > div {
      margin: 10px 0 0 0;

      & label {
        font-weight: bold;
      }
    }
  `,
  RightSideHeader: styled.h3`
    margin-top: 25px;
    text-align: center;
    text-transform: none;
    font-style: italic;
  `,
  SectionHeader: styled.h2`
    margin-top: 25px;
    background-image: linear-gradient(179deg, #2C7AC2 0%, #2C7AC2 50%,transparent 54%, transparent 100%);
    background-size:  160% 15%;
    background-repeat: repeat-x;
    background-position: left 0% bottom 10%;
    padding-bottom: 5px;
    text-transform: uppercase;
    width: fit-content;
  `,
  LeftSide: styled.div`
    word-wrap: break-word;
  `,
  RightSide: styled.div`
    max-width: 720px;
  `,
  Explanation: styled.div`
    display: flex;
    margin: 0 auto 20px;
    text-align: left;
  `,
  PageTitle: styled(PageTitle)`
    margin-top: 10px;
  `,

  Image: styled.img`
    align-self: flex-end;
    height: 20px;
    width: 20px;
    margin: 0 10px 10px 0;
    cursor: pointer;
    z-index: 1000;
  `,
  ProductLogo: styled.img`
    height: 70px;
    @media ${screenSize.small} {
      max-width: 290px;
    }
  `,
  Top: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
  `
}
